import { useMemo, useRef, useState } from 'react';
import { Experiment, useOptimizely } from '../lib/sdks/optimizely';
import { useNavigationInterceptor } from './use-navigation-interceptor';
import { useLocalStorage } from '@hooks/use-local-storage';

type UseDisplayFeedbackModalProps = {
  interceptLinkNavigation?: boolean;
  allowedExitRoutes?: string[];
};

export const useDisplayFeedbackModal = ({
  interceptLinkNavigation = false,
  allowedExitRoutes = [],
}: UseDisplayFeedbackModalProps = {}) => {
  const [seen, setSeen] = useLocalStorage('feedback_survey_answered', false);

  const [displayFeedbackModal, setDisplayFeedbackModal] = useState(false);
  const { decide, loading } = useOptimizely();
  const feedbackCallbackRef = useRef<(() => void) | null>(null);

  const feedbackSurveyEnabled: boolean = useMemo(() => {
    const decision = decide(Experiment.SAVE_JOB_FEEDBACK_SURVEY);
    return (
      !loading &&
      decision?.enabled &&
      decision?.variationKey === 'feedback-survey'
    );
  }, [decide, loading]);

  const includeFeedbackSurvey = feedbackSurveyEnabled && !seen;

  const setCallbackAndDisplayModal = (callback: () => void) => {
    setSeen(true);
    feedbackCallbackRef.current = callback;
    setDisplayFeedbackModal(true);
  };

  const onNavigationInterceptedCallback = (
    urlBeingIntercepted: string,
    continueNavigationMethod: () => void,
  ) => {
    const isAllowedExit = allowedExitRoutes.some(route =>
      urlBeingIntercepted.includes(route),
    );

    if (isAllowedExit) {
      return continueNavigationMethod();
    }

    setCallbackAndDisplayModal(continueNavigationMethod);
  };

  useNavigationInterceptor(
    onNavigationInterceptedCallback,
    interceptLinkNavigation && includeFeedbackSurvey,
  );

  const exitWithFeedbackCheck = (callback: () => void) => {
    if (includeFeedbackSurvey) {
      setCallbackAndDisplayModal(callback);
    } else {
      callback();
    }
  };

  return {
    feedbackCallback: feedbackCallbackRef.current,
    displayFeedbackModal,
    exitWithFeedbackCheck,
  };
};
